import { Alert, Box, CircularProgress, Divider, FormControl, FormLabel, Paper, Typography } from '@mui/material';
import type { IPermissionGroup } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import editDefaultPermissionGroup from '@src/APIs/graphQl/Company/editDefaultPermissionGroup';
import editMailEndings from '@src/APIs/graphQl/Company/editMailEndings';
import MultiTextChipSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/MultiTextChipSelect/MultiTextChipSelect';
import PermissionGroupSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/PermissionGroupSelect/PermissionGroupSelect';
import type Company from '@src/Objects/Company';
import { isValidEmailEnding } from '@src/Objects/Helper';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface RegistrationTabProps {
    company: Company;
    setCompany: (company: Company) => void;
}
/**
 * RegistrationTab component.
 *
 * @param {object} company - The company object.
 * @param {function} setCompany - The function to set the company object.
 * @returns {JSX.Element} - The rendered RegistrationTab component.
 */
export function RegistrationTab({ company, setCompany }: RegistrationTabProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const settingsStrings = getLanguageTexts(lang).settings;
    const langStrings = getLanguageTexts(lang).registrationConfig;
    const [mailError, setMailError] = useState<string | null>(null);
    const [pgError, setPgError] = useState<string | null>(null);
    const [savingEnding, setSavingEnding] = useState<boolean>(false);
    const [savingPG, setSavingPG] = useState<boolean>(false);

    const updateMailEndings = (newEndings: string[]) => {
        const lowerCaseEndings = newEndings.map((n) => n.toLocaleLowerCase());
        const inVailedEndings = lowerCaseEndings.filter((e) => !isValidEmailEnding(e));
        if (inVailedEndings.length > 0) {
            setMailError(
                inVailedEndings
                    .map((ending) => langStrings.emailEndingRegistration.errorMessage.replace('[ENDING]', ending))
                    .join('\n'),
            );
        } else {
            setMailError(null);
            setSavingEnding(true);
            editMailEndings(lowerCaseEndings)
                .then(setCompany)
                .catch((err) => setMailError(err.toString()))
                .finally(setSavingEnding.bind(null, false));
        }
    };

    const updatePermissionGroup = (permissionGroup: IPermissionGroup) => {
        if (company.defaultPermissionGroup?.id === permissionGroup.id) return;
        setPgError(null);
        setSavingPG(true);
        editDefaultPermissionGroup(permissionGroup.id)
            .then(setCompany)
            .catch((err) => setPgError(err.toString()))
            .finally(setSavingPG.bind(null, false));
    };

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {settingsStrings.registration}
                </Typography>
                <Divider />
                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        {langStrings.defaultRegistration.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {langStrings.defaultRegistration.text}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        {langStrings.emailEndingRegistration.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {langStrings.emailEndingRegistration.text}
                    </Typography>

                    <FormControl fullWidth>
                        <FormLabel>{langStrings.emailEndingRegistration.endingsField}</FormLabel>
                        {savingEnding ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <MultiTextChipSelect
                                value={company.allowedMailEndings}
                                onChange={updateMailEndings}
                                placeholder="@test.de"
                            />
                        )}
                        {mailError && (
                            <Alert severity="error" onClose={() => setMailError(null)} sx={{ mt: 1 }}>
                                {mailError}
                            </Alert>
                        )}
                    </FormControl>
                </Box>

                <FormControl fullWidth>
                    <FormLabel>{langStrings.permissionGroup}</FormLabel>
                    {savingPG ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <PermissionGroupSelect
                            permissionGroups={company.permissionGroups}
                            selectedPermissionGroup={
                                company.defaultPermissionGroup ? company.defaultPermissionGroup.id : -1
                            }
                            setSelectedPermissionGroup={updatePermissionGroup}
                        />
                    )}
                    {pgError && (
                        <Alert severity="error" onClose={() => setPgError(null)} sx={{ mt: 1 }}>
                            {pgError}
                        </Alert>
                    )}
                </FormControl>
            </Box>
        </Paper>
    );
}
